import { type AxiosResponse } from 'axios';

import type Address from '~/types/Address';

import { type Option } from '~/components/Select';
import { type JSONAddress } from '~/types/Address';
import { fromJSON as addressFromJSON } from '~/types/Address';

export const MANIFEST_FAQ_LINK =
  'https://support.goshippo.com/hc/en-us/articles/204520339-How-do-I-print-an-end-of-day-report-manifest-for-a-particular-carrier-';

type Manifest = {
  addressFrom: string;
  carrier?: string;
  documents: string[];
  errors: string[];
  id?: number;
  isTest?: Maybe<boolean>;
  objectId: string;
  objectStatus: ManifestStatus;
  providerToken: string;
  submissionDate: Date;
};

type JSONManifest = {
  // TODO: hydrate this on the BE
  address_from: string;
  carrier?: string;
  documents: string[];
  errors: string[];
  id?: number;
  object_id: string;
  object_status: ManifestStatus;
  provider: string;
  shipment_date: string;
};

const ManifestFromJSON = (manifestJSON: JSONManifest): Manifest => ({
  addressFrom: manifestJSON.address_from,
  carrier: manifestJSON.carrier,
  documents: manifestJSON.documents,
  errors: manifestJSON.errors,
  id: manifestJSON.id,
  objectId: manifestJSON.object_id,
  objectStatus: manifestJSON.object_status,
  providerToken: manifestJSON.provider,
  submissionDate: new Date(manifestJSON.shipment_date),
});

type ManifestsGetResponse = AxiosResponse<{
  results: JSONManifest[];
}>;

type ManifestsCreateType = {
  address: Option;
  carrierName: Option;
  dateRangeEnd?: Date;
  dateRangeStart: Date;
};

type ManifestsTransactionCount = {
  count: number;
  servicelevelName: string;
};

type ManifestServiceLevelCounts = {
  dateRangeEnd: Date;
  dateRangeStart: Date;
  manifestableTransactionCount: ManifestsTransactionCount[];
  submissionDate: Date;
};
type JSONManifestServiceLevelCounts = {
  date_range_end: Date;
  date_range_start: Date;
  manifestable_transaction_count: {
    count: number;
    servicelevel_name: string;
  }[];
  submission_date: Date;
};

const ManifestServiceLevelCountsFromJSON = (
  jsonManifestServiceLevelCounts: JSONManifestServiceLevelCounts,
): ManifestServiceLevelCounts => ({
  dateRangeEnd: jsonManifestServiceLevelCounts.date_range_end,
  dateRangeStart: jsonManifestServiceLevelCounts.date_range_start,
  manifestableTransactionCount: jsonManifestServiceLevelCounts.manifestable_transaction_count.map(
    (entry) => ({
      count: entry.count,
      servicelevelName: entry.servicelevel_name,
    }),
  ),
  submissionDate: jsonManifestServiceLevelCounts.submission_date,
});

type ManifestsDateRangeByAddress = {
  [key: string]: ManifestServiceLevelCounts[];
};

type JSONManifestsDateRangeByAddress = {
  [key: string]: JSONManifestServiceLevelCounts[];
};

type ManifestGroupsResponse = AxiosResponse<{
  results: JSONManifestGroups;
}>;

export type ManifestGroupsRequestPayload = {
  carrierObjectId: string;
  dateRangeEnd?: Date;
  dateRangeStart?: Date;
  submissionDate?: Date;
};

type JSONManifestGroups = {
  addresses: JSONAddress[];
  carrier_account: string;
  ranges: JSONManifestsDateRangeByAddress;
};

type ManifestGroups = {
  addresses: Address[];
  carrierAccount: string;
  ranges: ManifestsDateRangeByAddress;
};

const ManifestGroupsFromJSON = (jsonManifestGroups: JSONManifestGroups): ManifestGroups => ({
  addresses: jsonManifestGroups.addresses.map(
    (jsonAddress) => addressFromJSON(jsonAddress) as Address,
  ),
  carrierAccount: jsonManifestGroups.carrier_account,
  ranges: Object.entries(jsonManifestGroups.ranges).reduce(
    (rangesMap: ManifestsDateRangeByAddress, [key, jsonServiceLevelCountsList]) => {
      rangesMap[key] = jsonServiceLevelCountsList.map(ManifestServiceLevelCountsFromJSON);
      return rangesMap;
    },
    {},
  ),
});

//OBJECT_STATUS_CHOICE = Choices('QUEUED', 'SUCCESS', 'ERROR')
enum ManifestStatus {
  ERROR = 'ERROR',
  QUEUED = 'QUEUED',
  SUCCESS = 'SUCCESS',
}

type ManifestGeneratePayload = {
  address_from: Address;
  async: boolean;
  carrier_account: string;
  object_created?: string;
  object_updated?: string;
  submission_date: string;
};

export {
  JSONManifest,
  JSONManifestGroups,
  ManifestFromJSON,
  ManifestGeneratePayload,
  ManifestGroups,
  ManifestGroupsFromJSON,
  ManifestGroupsResponse,
  ManifestServiceLevelCounts,
  ManifestStatus,
  ManifestsCreateType,
  ManifestsDateRangeByAddress,
  ManifestsGetResponse,
  ManifestsTransactionCount,
};
export default Manifest;
